import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import clsx from 'clsx';
import { useKanbanBoardCardStyles } from 'components/UI/V2/KanbanBoard/Card/KanbanBoardCard.styles';
import React from 'react';

export interface KanbanBoardCardProps {
    id: string;
    columnId: string;
    rowId?: string;
    draggable?: boolean;
    className?: string;
    children?: React.ReactNode;
}

export const KanbanBoardCard: React.FC<KanbanBoardCardProps> = ({
    id,
    columnId,
    rowId,
    draggable: isDraggable = true,
    className,
    children
}) => {
    const classes = useKanbanBoardCardStyles();

    const cardRef = React.useRef<HTMLDivElement>(null);
    const suppressClickRef = React.useRef(false);

    const [isDragging, setIsDragging] = React.useState(false);

    React.useEffect(() => {
        const element = cardRef.current;
        if (!element || !isDraggable) return;

        return draggable({
            element,
            canDrag: ({ input }) => {
                const hit = document.elementFromPoint(input.clientX, input.clientY);
                return !(hit instanceof Element && hit.closest('[data-no-drag]'));
            },
            getInitialData: () => ({
                cardId: id,
                type: 'kanban-card',
                columnId,
                rowId
            }),
            onDragStart: () => {
                suppressClickRef.current = true;
                setIsDragging(true);
            },
            onDrop: () => {
                setIsDragging(false);
                window.setTimeout(() => {
                    suppressClickRef.current = false;
                }, 100);
            }
        });
    }, [id, columnId, isDraggable, rowId]);

    return (
        <div
            ref={cardRef}
            data-card-id={id}
            className={clsx(classes.root, className, {
                [classes.dragging]: isDragging,
                [classes.static]: !isDraggable
            })}
            onClickCapture={event => {
                if (!suppressClickRef.current) {
                    return;
                }
                suppressClickRef.current = false;
                event.preventDefault();
                event.stopPropagation();
            }}
        >
            {children}
        </div>
    );
};
